import { Box, Button, Typography, useTheme } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import { HOOK_COPY } from "../onboardingSteps";
import { OrbitCloud } from "../assets/OrbitCloud";
import { riseIn } from "../onboardingMotion";
import { displayTitleSx, primaryButtonSx, textBody, textPrimary } from "./stepStyles";

interface HookStepProps {
  onStart: () => void;
  onSignIn: () => void;
}

export const HookStep = ({ onStart, onSignIn }: HookStepProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const reduced = useReducedMotion();

  const rise = (delay: number) => ({
    variants: riseIn(reduced ? 0 : delay),
    initial: "hidden" as const,
    animate: "visible" as const,
  });

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", textAlign: "center" }}>
      {/* Copy block up top; the orbit centers itself in whatever space is
          left between the body text and the CTAs. */}
      <Box sx={{ pt: 4, display: "flex", flexDirection: "column", gap: 3 }}>
        <motion.div {...rise(0)}>
          <Box
            component="img"
            src={isDark ? "/insightLongLogoWhite.svg" : "/insightLongLogoPrimary.svg"}
            alt="Insight Analytics"
            sx={{ height: 32, width: "auto" }}
          />
        </motion.div>
        <motion.div {...rise(0.08)}>
          <Typography sx={{ ...displayTitleSx(isDark), fontSize: "2rem" }}>
            {HOOK_COPY.title}
          </Typography>
        </motion.div>
        <motion.div {...rise(0.16)}>
          <Typography
            sx={{ fontSize: "1rem", lineHeight: 1.5, color: textBody(isDark), maxWidth: 330, mx: "auto" }}
          >
            {HOOK_COPY.body}
          </Typography>
        </motion.div>
      </Box>

      <Box
        component={motion.div}
        {...rise(0.24)}
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <OrbitCloud />
      </Box>

      <motion.div {...rise(0.32)}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 3 }}>
          <Button
            fullWidth
            onClick={onStart}
            sx={{
              ...primaryButtonSx,
              transition: "background-color 0.15s ease, transform 0.1s ease",
              "&:active": { transform: "scale(0.98)" },
            }}
          >
            {HOOK_COPY.cta}
          </Button>
          {/* Glass secondary, matching the auth screens' button system. */}
          <Button
            fullWidth
            onClick={onSignIn}
            sx={{
              height: 48,
              fontWeight: 600,
              fontSize: "0.9375rem",
              textTransform: "none",
              borderRadius: 2.5,
              color: textPrimary(isDark),
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(255, 255, 255, 0.5)",
              backdropFilter: "blur(12px)",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
              transition:
                "background-color 0.15s ease, border-color 0.15s ease, transform 0.1s ease",
              "&:hover": {
                backgroundColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(255, 255, 255, 0.7)",
                borderColor: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.18)",
              },
              "&:active": { transform: "scale(0.97)" },
            }}
          >
            I already have an account
          </Button>
        </Box>
      </motion.div>
    </Box>
  );
};
